import { useState, useEffect, useCallback } from "react";
import api from "./api";
import { getErrorMessage } from "./errorHelper";
import { getOverview } from "../services/monitoringService.js";
import { REFRESH_INTERVAL_MS } from "../components/dashboard/dashboardHelpers.js";

/**
 * Charge NAS + passerelles UniFi (une seule requête /monitoring/admin/overview)
 * et serveurs Atera, puis les rafraîchit tout seul toutes les 30 s tant que
 * l'onglet du navigateur est visible. Les deux sources sont indépendantes :
 * si l'une échoue, l'autre s'affiche quand même (erreur par source).
 */
export default function useSupervisionData(intervalMs = REFRESH_INTERVAL_MS) {
  const [nas, setNas]               = useState([]);
  const [gateways, setGateways]     = useState([]);
  const [servers, setServers]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [errors, setErrors]         = useState({ monitoring: "", servers: "" });

  const load = useCallback(async (background = false) => {
    if (background) setRefreshing(true);
    const [mon, srv] = await Promise.allSettled([
      getOverview(),
      api.get("/assets/admin/all", { params: { type: "SERVER" } }),
    ]);

    const next = { monitoring: "", servers: "" };
    if (mon.status === "fulfilled") {
      setNas(mon.value?.nas ?? []);
      setGateways(mon.value?.gateways ?? []);
    } else {
      next.monitoring = mon.reason?.message || "Erreur de chargement NAS / UniFi";
    }
    if (srv.status === "fulfilled") {
      setServers(srv.value.data?.data ?? []);
    } else {
      next.servers = getErrorMessage(srv.reason);
    }
    setErrors(next);
    setLastRefresh(new Date());
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => {
    load(false);
    const id = setInterval(() => { if (!document.hidden) load(true); }, intervalMs);
    return () => clearInterval(id);
  }, [load, intervalMs]);

  return { nas, gateways, servers, loading, refreshing, lastRefresh, errors, reload: () => load(true), intervalMs };
}